/**
 * Session lifecycle and the single-flight refresh — docs/spec/06 R1.
 *
 * This is the highest-risk function in the codebase. The failure it exists to
 * prevent is subtle: a refresh implemented per-request works perfectly in a
 * single-request test and collapses in production, where a screen fires five
 * queries at once, all five 401 together, and five refreshes race — four of
 * which use a token the winner has already rotated.
 *
 * So: ONE in-flight refresh promise, shared by every concurrent caller.
 */
import { NetworkError } from './errors'
import { accessTokenNeedsRefresh, refreshAccessToken, useAuthStore } from './auth'

/** Called when the session is definitively over — the app routes to /login. */
export type SessionEndedHandler = (reason: 'expired' | 'logout') => void

let sessionEndedHandler: SessionEndedHandler | null = null

/**
 * The app registers how to react to a dead session. Kept as a callback so the
 * api layer never imports the router — 01 §2's boundary runs both ways.
 */
export function registerSessionEndedHandler(handler: SessionEndedHandler | null): void {
  sessionEndedHandler = handler
}

let inFlightRefresh: Promise<boolean> | null = null

/**
 * Why the session ended, read once by the login screen.
 *
 * Set synchronously so it cannot race the router. The alternative — inferring
 * intent from whichever navigation happens to win — is exactly the race this
 * replaces: on logout the store clears, the route guard fires, and it attaches
 * `?redirect=<the previous user's job>` to the login URL. On a shared
 * workstation the next person would then be dropped into work that is not
 * theirs.
 */
let lastEndReason: 'expired' | 'logout' | null = null

/** Read and clear the reason the session ended. */
export function consumeSessionEndReason(): 'expired' | 'logout' | null {
  const reason = lastEndReason
  lastEndReason = null
  return reason
}

/** Tear the session down and tell the app. */
export function endSession(reason: 'expired' | 'logout' = 'expired'): void {
  lastEndReason = reason
  useAuthStore.getState().clear()
  sessionEndedHandler?.(reason)
}

async function performRefresh(): Promise<boolean> {
  const { refreshToken } = useAuthStore.getState()

  // Nothing to refresh with: the session is already over.
  if (refreshToken === null) {
    endSession('expired')
    return false
  }

  try {
    useAuthStore.getState().setAccessToken(await refreshAccessToken(refreshToken))
    return true
  } catch (error) {
    if (error instanceof NetworkError) {
      /**
       * The network failed — we never learned whether the token is still good.
       *
       * Deliberately does NOT end the session. 06 R1 says a refresh *failure*
       * ends the session, but it means a refusal, not a dropped connection:
       * signing a transcriber out mid-shift because the wifi blipped would
       * destroy their unsaved work for no reason. The request fails, the
       * tokens survive, and the next attempt can succeed.
       */
      return false
    }

    // Anything else — a RefreshFailedError, or an unexpected throw — means we
    // cannot vouch for the session. End it rather than leave the user in a
    // state where every request quietly fails.
    endSession('expired')
    return false
  }
}

/**
 * Refresh the access token, at most once concurrently.
 *
 * Every caller that arrives while a refresh is running awaits the SAME promise
 * and gets the same answer. Returns `true` when a fresh access token is in the
 * store and the caller should retry.
 */
export function refreshSessionOnce(): Promise<boolean> {
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null
  })
  return inFlightRefresh
}

/**
 * Refresh ahead of expiry so most requests never 401 at all — 06 R1's
 * proactive refresh. A no-op unless the access token is inside the skew window.
 */
export async function ensureFreshAccessToken(): Promise<void> {
  if (accessTokenNeedsRefresh()) await refreshSessionOnce()
}

/** User-initiated sign-out (06 §5). */
export function logout(): void {
  endSession('logout')
}

/** Test-only: drop any in-flight refresh between cases. */
export function resetSessionForTests(): void {
  inFlightRefresh = null
  sessionEndedHandler = null
  lastEndReason = null
}
